import { useState } from 'react'

import { Box, Checkbox, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

interface DuplicateOptionsProps {
  canCreateSubproject: boolean
  canAssignEmployees: boolean
  onOptionsChange: (
    includeAll: boolean,
    includeSubprojects: boolean,
    includeAssignees: boolean,
    includeTasks: boolean,
    includeProjectTasksAssignees: boolean
  ) => void
}

export const DuplicateOptions: React.FC<DuplicateOptionsProps> = ({
  canCreateSubproject,
  canAssignEmployees,
  onOptionsChange,
}) => {
  const [includeAll, setIncludeAll] = useState(true)
  const [includeSubprojects, setIncludeSubprojects] = useState(true)
  const [includeAssignees, setIncludeAssignees] = useState(true)
  const [includeTasks, setIncludeTasks] = useState(true)
  const [includeProjectTasksAssignees, setIncludeProjectTasksAssignees] = useState(true)

  const changeGroupIncluding = (value: boolean) => {
    setIncludeAll(value)
    setIncludeSubprojects(value)
    setIncludeAssignees(value)
    setIncludeTasks(value)
    setIncludeProjectTasksAssignees(value)
  }

  useEffect(() => {
    onOptionsChange(
      includeAll,
      includeSubprojects,
      includeAssignees,
      includeTasks,
      includeProjectTasksAssignees
    )
  }, [includeAll, includeSubprojects, includeAssignees, includeTasks, includeProjectTasksAssignees])

  return (
    <Box background="grey100" borderRadius={{ all: 'abs016' }}>
      <Box
        background="grey000White"
        flexDirection="row"
        justifyContent="spaceBetween"
        border={{ all: { width: 's1', color: 'grey400', style: 'solid' } }}
        borderRadius={{ all: 'abs016' }}
        paddingX="s16"
        paddingY="s16"
      >
        <>
          <Text size="300" weight="semibold">
            {i18n.t('project_management.modals.duplicate_project.included_options.duplicate_all')}
          </Text>
          <Text size="300" weight="normal">
            {i18n.t('project_management.modals.duplicate_project.included_options.description')}
          </Text>
        </>
        <TogglerCheckbox
          value={includeAll}
          onChange={(value) => changeGroupIncluding(value)}
          disabled={false}
        />
      </Box>

      {!includeAll && (
        <Box
          gap="s16"
          paddingX="s16"
          paddingY="s16"
          borderRadius={{ bottomLeft: 'abs008', bottomRight: 'abs008' }}
        >
          {canAssignEmployees && (
            <Box gap="s16">
              <Text size="300">
                {i18n.t(
                  'project_management.modals.duplicate_project.included_options.individually'
                )}
              </Text>

              <Checkbox
                checked={includeAssignees}
                label={i18n.t(
                  'project_management.modals.duplicate_project.included_options.assignees'
                )}
                size="small"
                onChange={(value) => {
                  setIncludeAssignees(value.target.checked)
                }}
              />
            </Box>
          )}
          {canCreateSubproject && (
            <Checkbox
              checked={includeSubprojects}
              label={i18n.t(
                'project_management.modals.duplicate_project.included_options.subprojects'
              )}
              size="small"
              onChange={(value) => {
                setIncludeSubprojects(value.target.checked)
              }}
            />
          )}
          <Checkbox
            checked={includeTasks}
            label={i18n.t('project_management.modals.duplicate_project.included_options.tasks')}
            size="small"
            onChange={(value) => {
              setIncludeTasks(value.target.checked)
            }}
          />
          {includeAssignees && includeTasks && (
            <Box paddingLeft="s16" flexDirection="row">
              <Checkbox
                checked={includeProjectTasksAssignees}
                label={i18n.t(
                  'project_management.modals.duplicate_project.included_options.tasks_options.assignees.add'
                )}
                size="small"
                onChange={(value) => {
                  setIncludeProjectTasksAssignees(value.target.checked)
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
